/**
 * 将数字格式的文件大小转换成Kb, Mb, Gb格式的显示内容
 * @param value 要计算的文件大小
 * @param config 相关配置
 * @example $fileSize(1024) => '1.0 Kb'
 */
export default function (
	value: number,
	config: {
		base: number;
		bits: boolean;
		fullform: boolean;
		separator: string;
	} = { base: 2, bits: false, fullform: false, separator: '.' },
): any{
	var symbol = {
		iec:
			{
				bits: [ 'b', 'Kib', 'Mib', 'Gib', 'Tib', 'Pib', 'Eib', 'Zib', 'Yib' ],
				bytes: [ 'B', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB' ],
			},
		jedec:
			{
				bits: [ 'b', 'Kb', 'Mb', 'Gb', 'Tb', 'Pb', 'Eb', 'Zb', 'Yb' ],
				bytes: [ 'B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB' ],
			},
	};
	var fullform = {
		iec: [ '', 'kibi', 'mebi', 'gibi', 'tebi', 'pebi', 'exbi', 'zebi', 'yobi' ],
		jedec: [ '', 'kilo', 'mega', 'giga', 'tera', 'peta', 'exa', 'zetta', 'yotta' ],
	};

	var result: any = [],
		val: any = 0,
		e: any = void 0,
		base: any = void 0,
		bits: any = void 0,
		ceil: any = void 0,
		full: any = void 0,
		fullforms: any = void 0,
		locale: any = void 0,
		neg: any = void 0,
		num: any = void 0,
		output: any = void 0,
		round: any = void 0,
		unix: any = void 0,
		separator: any = void 0,
		spacer: any = void 0,
		standard: any = void 0,
		symbols: any = void 0;

	if (Object.prototype.toString.call(Number(value)) !== '[object Number]') {
		throw new TypeError('Invalid number');
	}

	bits = config.bits === true;
	unix = false;
	base = config.base || 2;
	round = 2;
	locale = '';
	separator = config.separator !== void 0 ? config.separator : '';
	spacer = unix ? '' : ' ';
	symbols = {};
	standard = 'jedec';
	output = 'string';
	full = config.fullform === true;
	fullforms = [];
	e = -1;
	num = Number(value);
	neg = num < 0;
	ceil = base > 2 ? 1000 : 1024;

	// Flipping a negative number to determine the size
	if (neg) {
		num = -num;
	}

	// Determining the exponent
	if (e === -1 || isNaN(e)) {
		e = Math.floor(Math.log(num) / Math.log(ceil));

		if (e < 0) {
			e = 0;
		}
	}

	// Exceeding supported length, time to reduce & multiply
	if (e > 8) {
		e = 8;
	}

	if (output === 'exponent') {
		return e;
	}

	// Zero is now a special case because bytes divide by 1
	if (num === 0) {
		result[0] = 0;
		result[1] = unix ? '' : (symbol as any)[standard][bits ? 'bits' : 'bytes'][e];
	} else {
		val = num / (base === 2 ? Math.pow(2, e * 10) : Math.pow(1000, e));

		if (bits) {
			val = val * 8;

			if (val >= ceil && e < 8) {
				val = val / ceil;
				e++;
			}
		}

		result[0] = Number(val.toFixed(e > 0 ? round : 0));
		result[1] =
			base === 10 && e === 1 ? (bits ? 'kb' : 'kB') : (symbol as any)[standard][bits ? 'bits' : 'bytes'][e];

		if (unix) {
			result[1] = standard === 'jedec' ? result[1].charAt(0) : e > 0 ? result[1].replace(/B$/, '') : result[1];

			if (/^(b|B)$/.test(result[1])) {
				result[0] = Math.floor(result[0]);
				result[1] = '';
			}
		}
	}

	// Decorating a 'diff'
	if (neg) {
		result[0] = -result[0];
	}

	// Applying custom symbol
	result[1] = symbols[result[1]] || result[1];

	if (locale === true) {
		result[0] = result[0].toLocaleString();
	} else if (locale.length > 0) {
		result[0] = result[0].toLocaleString(locale);
	} else if (separator.length > 0) {
		result[0] = result[0].toString().replace('.', separator);
	}

	// Returning Array, Object, or String (default)
	if (output === 'array') {
		return result;
	}

	if (full) {
		result[1] = fullforms[e]
			? fullforms[e]
			: (fullform as any)[standard][e] + (bits ? 'bit' : 'byte') + (result[0] === 1 ? '' : 's');
	}

	if (output === 'object') {
		return { value: result[0], symbol: result[1] };
	}

	return result.join(spacer);
}
